import {
  Component,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import Loader from './Loader'
import CustomButton from './CustomButton'
import { SvgUndo, SvgZoomIn, SvgZoomOut, SvgRedo } from '../assets/images'
import 'pdfjs-dist/web/pdf_viewer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { PDFDocumentProxy } from 'pdfjs-dist'

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

class PdfRenderErrorBoundary extends Component<
  { resetKey: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[40rem] min-w-full w-full flex items-center justify-center flex-1 text-sm text-accent-600">
          Unable to preview this PDF.
        </div>
      )
    }
    return this.props.children
  }
}

const getPdfFileSource = (base64String: string): string => {
  return `data:application/pdf;base64,${base64String}`
}

interface PdfViewerPropsType {
  pdfUrl: string
  isOutline?: boolean
  width?: string
  height?: string
  showLoader?: boolean
  isBase64?: boolean
  allowZoom?: boolean
  showPageNumber?: boolean
  defaultZoomScale?: number
  setTotalPages?: Dispatch<SetStateAction<number>>
  loadingMessage?: string
}

interface LoadingStateProps {
  loadingMessage?: string
}

// Might need later
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
  // S3/CORS often breaks range requests; avoid needing a blob pre-fetch.
  disableRange: true,
  disableStream: true,
}

const PdfLoadingState = ({ loadingMessage }: LoadingStateProps) => (
  <div className="min-h-[40rem] min-w-full w-full flex flex-col items-center justify-center flex-1">
    <Loader isGlobal={false} loaderHeight="3rem" loaderWidth="3rem" />
    {loadingMessage && (
      <span className="text-sm text-bold ml-2 py-3">{loadingMessage}</span>
    )}
  </div>
)

const PdfViewer = ({
  pdfUrl,
  isOutline = false,
  width = 'w-full',
  height = 'h-fit',
  showLoader = true,
  loadingMessage,
  isBase64,
  allowZoom = true,
  showPageNumber = true,
  defaultZoomScale = 1,
  setTotalPages,
}: PdfViewerPropsType) => {
  const [numPages, setNumPages] = useState<number>(0)
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const containerRef = useRef<HTMLDivElement>(null)

  const [scale, setScale] = useState<number>(defaultZoomScale ?? 1)
  const [rotation, setRotation] = useState<number>(0)
  const rotateLeft = () => setRotation((prev) => (prev - 90) % 360)
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360)
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3))
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5))

  const fileSource = useMemo(() => {
    if (!pdfUrl) return null
    if (isBase64) return getPdfFileSource(pdfUrl)
    return pdfUrl.replace(/\s+/g, '')
  }, [pdfUrl, isBase64])

  useEffect(() => {
    setNumPages(0)
    setCurrentPage(1)
  }, [fileSource])

  const prevFileSource = useRef(fileSource)
  if (prevFileSource.current !== fileSource) {
    prevFileSource.current = fileSource
    setNumPages(0)
    setCurrentPage(1)
  }

  const onDocumentLoadSuccess = async (pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages)
    setTotalPages && setTotalPages(pdf.numPages)
    pageRefs.current = Array(pdf.numPages).fill(null)
  }

  const onDocumentLoadError = () => {
    setNumPages(0)
  }

  const handleScroll = () => {
    const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0

    let visiblePage = pageRefs.current.length ? 1 : 0
    for (let i = 0; i < pageRefs.current.length; i++) {
      const page = pageRefs.current[i]
      if (page) {
        const { top } = page.getBoundingClientRect()
        if (top - containerTop < 300) {
          visiblePage = i + 1
        }
      }
    }

    setCurrentPage(visiblePage)
  }

  const handleZoomChange = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const value = parseInt(event.currentTarget.value, 10)
    if (!isNaN(value) && value >= 50 && value <= 300) {
      setScale(value / 100)
    }
  }

  useEffect(() => {
    setCurrentPage(pageRefs.current.length ? 1 : 0)
  }, [pageRefs.current.length])

  return (
    <>
      <div
        className={`action-btns flex justify-center p-1 gap-2 absolute w-full top-0 pt-[0.35rem] left-0 z-[1] ${
          !allowZoom ? 'hidden' : ''
        }`}
      >
        <span className="border inline-flex align-center border-color-[#E5E5E5] rounded-[0.25rem] bg-accent-0">
          <CustomButton
            type="button"
            color="primary"
            widthClass="w-[1.55rem]"
            height="h-[1.5rem]"
            horizontalPaddingClasses="px-0"
            className="border-accent-110"
            onClick={zoomOut}
            dataQaId="pdf-zoom-out"
          >
            <SvgZoomOut />
          </CustomButton>

          <input
            className="w-10 h-[1.5rem] text-[0.75rem] text-accent-600 pointer-events-none"
            type="text"
            value={`${Math.round(scale * 100)}%`}
            onKeyDown={(event) => handleZoomChange(event)}
            min={50}
            max={300}
          />
          <CustomButton
            type="button"
            color="primary"
            widthClass="w-[1.55rem]"
            height="h-[1.5rem]"
            horizontalPaddingClasses="px-0"
            className="border-accent-110"
            onClick={zoomIn}
            dataQaId="pdf-zoom-in"
          >
            <SvgZoomIn />
          </CustomButton>
          <CustomButton
            type="button"
            color="primary"
            widthClass="w-[1.55rem]"
            height="h-[1.5rem]"
            horizontalPaddingClasses="px-0"
            className="border-accent-110"
            onClick={rotateLeft}
            dataQaId="pdf-rotate-left"
          >
            <SvgUndo />
          </CustomButton>
          <CustomButton
            type="button"
            color="primary"
            widthClass="w-[1.55rem]"
            height="h-[1.5rem]"
            horizontalPaddingClasses="px-0"
            className="border-accent-110"
            onClick={rotateRight}
            dataQaId="pdf-rotate-right"
          >
            <SvgRedo />
          </CustomButton>
        </span>
      </div>

      <div
        className={`p-1 relative h-full py-0 bg-white overflow-y-auto pdf-wrap-box`}
        ref={containerRef}
        onScroll={handleScroll}
      >
        <div className={`${width} ${height} h-[56vh]`}>
          {fileSource ? (
            <PdfRenderErrorBoundary resetKey={fileSource}>
              <Document
                key={fileSource}
                file={fileSource}
                options={options}
                className={`${isOutline ? 'border-2 border-black' : ''}`}
                loading={
                  showLoader ? (
                    <PdfLoadingState loadingMessage={loadingMessage} />
                  ) : null
                }
                error={null}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                onSourceError={onDocumentLoadError}
              >
                {Array.from(new Array(numPages), (_el, index) => (
                  <div
                    className="flex justify-start items-start mb-4"
                    key={`${fileSource}_page_${index + 1}`}
                    ref={(el) => (pageRefs.current[index] = el)}
                  >
                    <Page
                      rotate={rotation}
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={scale}
                      className="text-left"
                    />
                  </div>
                ))}
              </Document>
            </PdfRenderErrorBoundary>
          ) : showLoader ? (
            <PdfLoadingState />
          ) : null}
        </div>
      </div>
      <span
        className={`page-count text-white align-top max-w-20 text-xs font-normal text-center leading-[2] absolute bottom-4 left-0 right-0 mx-auto rounded-md bg-black/60 ${
          !showPageNumber ? '!hidden' : ''
        }`}
      >
        {currentPage}/{numPages}
      </span>
    </>
  )
}

export default memo(PdfViewer)
